// src/middlewares/axiosInstance.ts
import axios from "axios";
import { authUtils } from "../modules/auth/utils/auth.utils";
import { authService } from "../services/auth.service";

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api`,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Add request interceptor with error logging
axiosInstance.interceptors.request.use(
  (config) => {
    // Log request details for debugging

    const token = authUtils.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Modify the response interceptor to handle errors better
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful response
    return response;
  },
  async (error) => {
    // Log error details

    const originalRequest = error.config;

    // Skip token refresh for auth endpoints
    const isAuthEndpoint = originalRequest.url?.includes("/auth/");
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // Only try to refresh for 401 errors on non-auth endpoints
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = authUtils.getRefreshToken();
        if (!refreshToken) throw new Error("No refresh token available");

        const response = await authService.refreshToken(refreshToken);
        authUtils.setTokens(response.accessToken, response.refreshToken);

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        authUtils.clearTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
